<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "~/composables/useI18n";

const t = useI18n();

const tabs = [
  { id: "tools", hash: "#tools", label: t("skillsPage.tabs.tools") },
  { id: "learning", hash: "#learning", label: t("skillsPage.tabs.learning") },
] as const;

type TabId = (typeof tabs)[number]["id"];

const activeTab = ref<TabId>("tools");

let observers: IntersectionObserver[] = [];

onMounted(() => {
  tabs.forEach(({ id }) => {
    const el = document.getElementById(id);
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            activeTab.value = id;
          }
        }
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );

    observer.observe(el);
    observers.push(observer);
  });
});

onUnmounted(() => {
  observers.forEach((o) => o.disconnect());
  observers = [];
});
</script>

<template>
  <nav
    aria-label="Page sections"
    class="sticky top-[var(--main-nav-height)] z-40 bg-white dark:bg-primary-950 border-b border-gray-200 dark:border-primary-800 shadow-sm"
  >
    <div role="tablist" :aria-label="t('skillsPage.pageTitle')" class="flex">
      <NuxtLink
        v-for="tab in tabs"
        :key="tab.id"
        :to="{ path: '/skills', hash: tab.hash }"
        role="tab"
        :aria-selected="activeTab === tab.id"
        :aria-controls="tab.id"
        :tabindex="activeTab === tab.id ? 0 : -1"
        class="px-6 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400"
        :class="
          activeTab === tab.id
            ? 'border-primary-600 text-primary-700 dark:text-primary-300 dark:border-primary-400'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
        "
      >
        {{ tab.label }}
      </NuxtLink>
    </div>
  </nav>
</template>
